import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';

export function LogsPanel() {
  const [tab, setTab] = useState<'calls' | 'audit'>('calls');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const { t } = useI18n();
  const fmt = useFormatters();

  const calls = useQuery({
    queryKey: ['api-calls', onlyErrors],
    queryFn: () => api.apiCalls(onlyErrors),
    enabled: tab === 'calls',
    refetchInterval: 10_000,
  });

  const audit = useQuery({
    queryKey: ['audit'],
    queryFn: api.auditLog,
    enabled: tab === 'audit',
  });

  const rate = useQuery({
    queryKey: ['rate-limit'],
    queryFn: api.rateLimit,
    refetchInterval: 15_000,
  });

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('logs.title')}</h2>
        <div className="segmented">
          <button
            type="button"
            className={tab === 'calls' ? 'active' : ''}
            onClick={() => setTab('calls')}
          >
            {t('logs.tab.calls')}
          </button>
          <button
            type="button"
            className={tab === 'audit' ? 'active' : ''}
            onClick={() => setTab('audit')}
          >
            {t('logs.tab.audit')}
          </button>
        </div>
      </div>

      <div className="notice">{t('logs.redactionNotice')}</div>

      {rate.data && (
        <div className={rate.data.paused ? 'notice warn' : 'meta-facts'}>
          {t('logs.rateUsage', {
            calls: rate.data.usage.callCount ?? 0,
            cpu: rate.data.usage.totalCpuTime ?? 0,
            time: rate.data.usage.totalTime ?? 0,
          })}
          {rate.data.paused && t('logs.ratePaused')}
        </div>
      )}

      {tab === 'calls' && (
        <>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={onlyErrors}
              onChange={(event) => setOnlyErrors(event.target.checked)}
            />
            {t('logs.failuresOnly')}
          </label>

          <div className="grid-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('jobs.when')}</th>
                  <th>{t('logs.method')}</th>
                  <th>{t('logs.endpoint')}</th>
                  <th>{t('jobs.http')}</th>
                  <th>{t('logs.duration')}</th>
                  <th>{t('jobs.metaCode')}</th>
                  <th>{t('jobs.message')}</th>
                  <th>fbtrace_id</th>
                </tr>
              </thead>
              <tbody>
                {calls.data?.calls.map((call) => (
                  <tr key={call.id} className={call.ok ? '' : 'row-error'}>
                    <td className="small">{fmt.time(call.createdAt)}</td>
                    <td className="mono tiny">{call.method}</td>
                    <td className="mono tiny">{call.path}</td>
                    <td>{call.httpStatus ?? '—'}</td>
                    <td className="small">{call.durationMs ?? '—'}</td>
                    <td className="mono tiny">{call.metaErrorCode ?? '—'}</td>
                    <td className="text-cell small">{call.metaErrorMessage ?? '—'}</td>
                    <td className="mono tiny">{call.fbtraceId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'audit' && (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('jobs.when')}</th>
                <th>{t('logs.actor')}</th>
                <th>{t('logs.action')}</th>
                <th>{t('logs.entity')}</th>
                <th>{t('logs.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {audit.data?.entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="small">{fmt.dateTime(entry.createdAt)}</td>
                  <td>{entry.actor}</td>
                  <td className="mono tiny">{entry.action}</td>
                  <td className="small">{entry.entity}</td>
                  <td className="text-cell tiny mono">
                    {entry.detail ? JSON.stringify(entry.detail) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
